<template>
  <div class="min-h-screen bg-white">
    <!-- Header -->
    <div class="border-b bg-white">
      <div class="max-w-4xl mx-auto px-6 py-4">
        <NuxtLink to="/" class="text-blue-600 hover:text-blue-700 font-medium">
          ← Back to Home
        </NuxtLink>
      </div>
    </div>

    <!-- Content -->
    <main class="max-w-4xl mx-auto px-6 py-12">
      <h1 class="text-3xl md:text-4xl font-extrabold text-slate-900 mb-8">
        Terms of Service
      </h1>

      <div class="prose prose-slate max-w-none">
        <p class="text-lg text-slate-600 mb-8">
          These Terms of Service govern your use of the GVGmall website and our digital software licensing services. By accessing or using our services, you agree to these terms.
        </p>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">About GVGmall</h2>
          <p class="text-slate-600">
            GVGmall provides digital software licensing solutions. We specialize in delivering genuine software licenses through secure digital delivery channels, accompanied by professional customer support services.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Use of Services</h2>
          <p class="text-slate-600 mb-4">
            By using our services, you agree to:
          </p>
          <ul class="list-disc pl-6 text-slate-600 space-y-2">
            <li>Use our website and services for lawful purposes only</li>
            <li>Provide accurate and truthful information when contacting support</li>
            <li>Not attempt to disrupt or interfere with our website or services</li>
            <li>Respect intellectual property rights of software vendors and GVGmall</li>
          </ul>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Digital Products and Delivery</h2>
          <p class="text-slate-600">
            All products offered are digital software licenses delivered via email. Delivery times are typically immediate after order confirmation, though some cases may require additional verification. License keys and activation instructions are sent to the email address provided during the order process.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Intellectual Property</h2>
          <p class="text-slate-600">
            All software products, trademarks, and brand names mentioned on our website are the property of their respective owners. GVGmall does not claim ownership of third-party software. We are an independent provider of software licensing solutions.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Support and Service</h2>
          <p class="text-slate-600">
            We provide customer support for license delivery and activation questions. Support is available via email and we strive to respond within 24 hours during business days. Our support covers technical assistance with license activation and general inquiries about our services.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Limitation of Liability</h2>
          <p class="text-slate-600">
            GVGmall provides software licenses on an "as is" basis. While we guarantee the authenticity of all licenses sold, we are not responsible for software compatibility issues, system requirements, or end-user usage. Please verify software requirements before purchase.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Modifications to Terms</h2>
          <p class="text-slate-600">
            We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting to this page. Continued use of our services after changes constitutes acceptance of the modified terms.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-bold text-slate-900 mb-4">Contact Information</h2>
          <p class="text-slate-600 mb-4">
            For questions about these Terms of Service or our services:
          </p>
          <p class="text-slate-900">
            <strong>Email:</strong> <a href="mailto:support@gvgmall.co" class="text-blue-600 hover:text-blue-700">support@gvgmall.co</a>
          </p>
        </section>

        <p class="text-sm text-slate-500 mt-12">
          Last updated: June 2025
        </p>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: 'institutional'
})

useSeoMeta({
  title: 'Terms of Service | GVGmall',
  description: 'GVGmall Terms of Service - Learn about the terms governing our digital software licensing services.',
  ogTitle: 'Terms of Service | GVGmall',
  ogDescription: 'GVGmall Terms of Service - Learn about the terms governing our services.',
  robots: 'index,follow'
})

useHead({
  link: [
    { rel: 'canonical', href: 'https://www.gvgmall.co/legal/terms-of-service' }
  ]
})
</script>
